/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Observable } from "@vienna-hypertext/observable";
import router from "next/router";
import queryString from "query-string";
import { v4 as uuid } from "uuid";

import { AtLeast, Coordinates } from "@vienna/common/src";
import { BaseBlockSnapshot, BlockSnapshot } from "@vienna/common/src/entities/block-snapshot.entity";

import { BasePlate } from "../plate/BasePlate/BasePlate";
import { Plate } from "../plate/Plate";
import { PlateSocket } from "../plate/PlateSocket/PlateSocket";
import { Auth } from "../user/auth/Auth";
import { EnvUtils } from "../utilities/EnvUtils";
import { API } from "src/classes/api";
import { BasicBlockMethods } from "src/classes/block/block.interfaces";
import { ClipboardUtils } from "src/classes/utilities/ClipboardUtils";
import { PositionUtils } from "src/classes/utilities/PositionUtils";

export class BaseBlock<T extends BlockSnapshot> extends Observable<T> implements BasicBlockMethods {
  plateSocket: PlateSocket | null;

  constructor(snapshot: T, plateSocket: PlateSocket | null) {
    super(snapshot);
    this.plateSocket = plateSocket;
  }

  /* -------------------------------------------------- */
  /*                    Move & Resize                   */
  /* -------------------------------------------------- */
  public move(top: number, left: number): void {
    if (!this.plateSocket) return;
    this.setState({ top: PositionUtils.snapToGrid(top), left: PositionUtils.snapToGrid(left) } as Partial<T>);
    this.plateSocket.saveBlockUpdate(this.snapshot, this.prevSnapshot);
  }

  public resize(width: number, height: number): void {
    if (!this.plateSocket) return;
    this.setState({
      width: PositionUtils.snapToGrid(width),
      height: PositionUtils.snapToGrid(height),
    } as Partial<T>);
    this.plateSocket.saveBlockUpdate(this.snapshot, this.prevSnapshot);
  }

  public changeZIndex(zIndex: number): void {
    if (!this.plateSocket) return;
    this.setState({ zIndex } as Partial<T>);
    this.plateSocket.saveBlockUpdate(this.snapshot, this.prevSnapshot);
  }

  /* -------------------------------------------------- */
  /*                   Socket Update                    */
  /* -------------------------------------------------- */
  // note: this function is a little dangerous, look for improvements.
  public handleIncomingSocketUpdate(snapshot: AtLeast<BlockSnapshot, "id">): void {
    this.setState({ ...snapshot } as Partial<T>);
  }

  /* -------------------------------------------------- */
  /*                  Like & Connect                    */
  /* -------------------------------------------------- */
  public async like(): Promise<void> {
    this.setState({ isLiked: !this.snapshot.isLiked } as Partial<T>);
    try {
      await API.blocks.like(this.snapshot.id);
    } catch {
      this.setState({ isLiked: !this.snapshot.isLiked } as Partial<T>); // revert if call fails
    }
  }

  public async connect(toPlate: BasePlate | Plate): Promise<void> {
    await API.blocks.connect(this.snapshot.id, toPlate);
    this.setState({ connectionCount: this.snapshot.connectionCount + 1 } as Partial<T>);
  }

  public get forbiddenConnectionIds(): string[] {
    return [this.snapshot.plateId];
  }

  /* -------------------------------------------------- */
  /*                    Block Link                      */
  /* -------------------------------------------------- */
  public open(): void {
    router.replace(this.internalLink, undefined, {
      shallow: true,
    });
  }

  public openInNewTab(): void {
    window.open(this.externalLink, "_blank", "noopener noreferrer");
  }

  public copyLink(): void {
    ClipboardUtils.copyTextToClipboard(this.externalLink);
  }

  private get internalLink(): string {
    return `${router.asPath}?${queryString.stringify({ blockId: this.snapshot.id })}`;
  }

  private get externalLink(): string {
    const topLevelDomain = EnvUtils.getTopLevelDomain();
    return `${topLevelDomain}/${this.internalLink}`;
  }

  /* -------------------------------------------------- */
  /*                       Utils                        */
  /* -------------------------------------------------- */
  public get coordinates(): Coordinates {
    return {
      top: this.snapshot.top,
      left: this.snapshot.left,
      width: this.snapshot.width,
      height: this.snapshot.height,
    };
  }

  /* -------------------------------------------------- */
  /*                        Create                      */
  /* -------------------------------------------------- */
  public static newBaseBlockSnapshot(
    args: Pick<BaseBlockSnapshot, "top" | "left" | "width" | "height" | "zIndex" | "plateId">
  ): BaseBlockSnapshot {
    const currentUser = Auth.instance.user!;
    const snapshot: BaseBlockSnapshot = {
      width: PositionUtils.snapToGrid(args.width),
      height: PositionUtils.snapToGrid(args.height),
      top: PositionUtils.snapToGrid(args.top),
      left: PositionUtils.snapToGrid(args.left),
      plateId: args.plateId,
      zIndex: args.zIndex,
      id: uuid(),
      createdAt: new Date().toString(),
      isLiked: false,
      connectionCount: 0,
      currentUserIsOwner: true,
      userId: currentUser.snapshot.id,
    };
    return snapshot;
  }
}
